/**
 * Emoji removal for the copy surface, and for nothing that renders.
 *
 * It lives outside `emoji-typesetting.ts` on purpose. That module is the render
 * path's token model, imported by four components; a strip-everything helper
 * sitting beside them is one autocomplete away from deleting every semantic
 * glyph on screen. Here the filename is the guard.
 */

import emojiRegex from "emoji-regex";

import { POLLY_EDITORIAL_V1 } from "./editorial-policy";
import {
    canonicalEmojiKey,
    expandLeft,
    expandRight,
    firstVisible,
    lastVisible,
    WORD_LIKE,
} from "./emoji-typesetting";

/**
 * The manifest's pinned notation, keyed the way a match is looked up.
 *
 * Canonical rather than raw, which is where this deliberately parts company
 * with `resolveDisposition`: on the render path an emoji-presented `©️` is a
 * token the vocabulary may still classify, but here the fallback is removal,
 * so presentation would decide whether a copyright sign survives being copied.
 */
const PRESERVED_CANONICAL_KEYS: ReadonlySet<string> = new Set(
    POLLY_EDITORIAL_V1.preservedSymbols.map(canonicalEmojiKey)
);

const SPAN_DELIMITERS = new Set(["*", "_", "~", "`"]);
const OPENING_PUNCTUATION = new Set(["(", "[", "{", "*", "_", "~", "`"]);
const CLOSING_PUNCTUATION = new Set([
    ")",
    "]",
    "}",
    ".",
    ",",
    ";",
    ":",
    "!",
    "?",
    "*",
    "_",
    "~",
    "`",
]);
const LINE_BREAKS = new Set(["\n", "\r"]);
const URL_TOKEN = /(?:https?:\/\/|mailto:|www\.)\S*$/iu;
const WHITESPACE = /\s/u;

interface MarkSpan {
    readonly start: number;
    readonly end: number;
}

/**
 * Widen a removal to take the delimiter pair it just emptied.
 *
 * Left alone, `**✅**` reduces to `****`, which is a thematic break, and
 * `~~✅~~` reduces to `~~~~`, which opens a code fence — the removal would
 * insert block structure nobody wrote. When the mark WAS the whole span, its
 * delimiters are debris on the same argument the mark is.
 *
 * Only a pair the mark filled entirely is taken, which is what the outward
 * flanking test decides: in `**bold**✅**more**` the two runs close and open
 * different spans, and fusing them would merge the two into one.
 */
const widenEmptiedSpan = (
    text: string,
    mark: MarkSpan,
    floor: number
): MarkSpan => {
    const delimiter = mark.start > floor ? text.charAt(mark.start - 1) : "";
    if (
        !SPAN_DELIMITERS.has(delimiter) ||
        text.charAt(mark.end) !== delimiter
    ) {
        return mark;
    }

    let left = mark.start;
    while (left > floor && text.charAt(left - 1) === delimiter) left -= 1;

    let right = mark.end;
    while (right < text.length && text.charAt(right) === delimiter) right += 1;

    const outerBefore = lastVisible(text, left);
    const outerAfter = firstVisible(text, right);
    if (
        outerBefore !== undefined &&
        WORD_LIKE.test(outerBefore) &&
        outerAfter !== undefined &&
        WORD_LIKE.test(outerAfter)
    ) {
        return mark;
    }

    const width = Math.min(mark.start - left, right - mark.end);
    return { start: mark.start - width, end: mark.end + width };
};

/**
 * What replaces one removal, whitespace included.
 *
 * The rule the render path uses cannot serve here. `toDisplayText` strips the
 * whitespace on BOTH sides and decides whether one space goes back, which is
 * right for rendered prose — but this runs on markdown source, where a space is
 * syntax. A space put back after `**` cancels the emphasis outright, and
 * `**⚠️ Bold**` copied out as `** Bold**`: literal asterisks in the plain
 * flavour, and literal asterisks in the HTML flavour too, because the pipeline
 * parses the same broken source.
 *
 * So: consume the authored whitespace on ONE side and leave the other side
 * exactly as written. Nothing is invented except in the one case where the mark
 * was wedged between two words with no whitespace at all to inherit.
 */
const bridgeRemoval = (
    text: string,
    outer: MarkSpan,
    mark: MarkSpan
): string => {
    const left = text.slice(outer.start, mark.start);
    const right = text.slice(mark.end, outer.end);

    if (left.length > 0 && right.length > 0) return left;

    const before = lastVisible(text, outer.start);
    const after = firstVisible(text, outer.end);

    if (left.length > 0) {
        return after === undefined ||
            LINE_BREAKS.has(after) ||
            CLOSING_PUNCTUATION.has(after) ||
            (before !== undefined && OPENING_PUNCTUATION.has(before))
            ? ""
            : left;
    }

    if (right.length > 0) {
        return before === undefined ||
            LINE_BREAKS.has(before) ||
            OPENING_PUNCTUATION.has(before) ||
            (after !== undefined && CLOSING_PUNCTUATION.has(after))
            ? ""
            : right;
    }

    return before !== undefined &&
        after !== undefined &&
        WORD_LIKE.test(before) &&
        WORD_LIKE.test(after)
        ? isUrlTokenAt(text, mark.start)
            ? ""
            : " "
        : "";
};

const isUrlTokenAt = (text: string, index: number): boolean => {
    let start = index;
    while (start > 0 && !WHITESPACE.test(text.charAt(start - 1))) start -= 1;
    return URL_TOKEN.test(text.slice(start, index));
};

/**
 * Remove every pictographic mark, keeping only the manifest's pinned notation.
 *
 * The copy surface's rule, and it is NOT the render path's: typesetting keeps
 * unclassified marks because absence from the vocabulary is not evidence of
 * decorativeness, while a pasted answer is a document, where the glyphs cannot
 * travel and a bare emoji is noise.
 *
 * `emoji-regex` matches © ® ™ and ↔ as well, so the carve-out is what keeps a
 * carrier's ™ and an authored arrow out of the removal set.
 */
export const stripAllEmojis = (text: string): string => {
    const marks = removableMarks(text);
    // Emoji-free prose is returned untouched rather than normalised: the plain
    // flavour is meant to be today's copy output minus the emojis, so a text
    // with none of them must come back byte-identical.
    if (marks.length === 0) return text;

    let out = "";
    let cursor = 0;
    let index = 0;

    while (index < marks.length) {
        const removal = resolveRemoval(text, marks, index, cursor);

        out += text.slice(cursor, removal.outer.start);
        out += removal.replacement;
        cursor = removal.outer.end;
        index = removal.nextIndex;
    }

    return out + text.slice(cursor);
};

export const removeEmojiSequences = (text: string): string => {
    const marks = removableMarks(text);
    if (marks.length === 0) return text;

    let out = "";
    let cursor = 0;
    for (const mark of marks) {
        out += text.slice(cursor, mark.start);
        cursor = mark.end;
    }
    return out + text.slice(cursor);
};

interface Removal {
    readonly outer: MarkSpan;
    readonly replacement: string;
    readonly nextIndex: number;
}

/** What one removal consumes and leaves behind, marks merged and span widened. */
const resolveRemoval = (
    text: string,
    marks: readonly MarkSpan[],
    index: number,
    floor: number
): Removal => {
    // Adjacent marks are bridged once, not once each. Resolved separately, the
    // second removal sees a left side whose whitespace the first one already
    // consumed, and `⚠️🔴 Alert` loses the space before "Alert".
    let mark = marks[index];
    let right = expandRight(text, mark.end);
    let nextIndex = index + 1;

    while (nextIndex < marks.length && marks[nextIndex].start <= right) {
        mark = { start: mark.start, end: marks[nextIndex].end };
        right = Math.max(right, expandRight(text, mark.end));
        nextIndex += 1;
    }

    // After the merge, so a span emptied by several marks is recognised as
    // emptied — `**✅ ⚠️**` is one removal, not two.
    mark = widenEmptiedSpan(text, mark, floor);
    const outer = {
        start: expandLeft(text, mark.start, floor),
        end: Math.max(right, expandRight(text, mark.end)),
    };

    return { outer, replacement: bridgeRemoval(text, outer, mark), nextIndex };
};

/**
 * Every match the manifest does not pin, as source offsets.
 *
 * The expression is built per call rather than hoisted: it carries the global
 * flag, so a shared instance would be mutable state to reset and defend, and
 * this runs once per copy click — never per streaming token, which is the only
 * reason the render path's matcher is hoisted at all.
 */
const removableMarks = (text: string): MarkSpan[] => {
    const matcher = emojiRegex();
    const marks: MarkSpan[] = [];

    let match = matcher.exec(text);
    while (match !== null) {
        const start = match.index;
        const end = start + match[0].length;

        // A zero-length match would never advance `lastIndex` and the loop
        // would not terminate. The matcher should not produce one; this costs
        // nothing and removes the possibility.
        if (end === start) {
            matcher.lastIndex = start + 1;
            match = matcher.exec(text);
            continue;
        }

        if (!PRESERVED_CANONICAL_KEYS.has(canonicalEmojiKey(match[0]))) {
            marks.push({ start, end });
        }
        match = matcher.exec(text);
    }

    return marks;
};
